import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaWallet, FaHistory, FaArrowCircleUp, FaCheckCircle, FaClock, FaTimesCircle, FaInfoCircle, FaRedo } from "react-icons/fa";
import { useAppContext } from "../context/AppContext";
import WithdrawalNoticeModal from "./BinaceIssueInfo";

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  purple: "#8b5cf6",
  purpleDim: "rgba(139,92,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  successDim: "rgba(34,197,94,0.1)",
  danger: "#ef4444",
  warning: "#f59e0b",
};

const QuickStat = ({ label, val, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
    whileHover={{ y: -5 }}
    style={{
      background: T.bgCard,
      border: `1px solid ${T.border}`,
      borderRadius: 22,
      padding: "clamp(16px, 3vw, 22px) clamp(12px, 3vw, 18px)",
      textAlign: "center",
      boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
      transition: "border-color 0.25s",
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = `${color}55`}
    onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
  >
    <p style={{ fontSize: "clamp(8px, 2vw, 9px)", fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 8, whiteSpace: "nowrap" }}>
      {label}
    </p>
    <div style={{ fontSize: "clamp(1.1rem, 3vw, 1.3rem)", fontWeight: 900, color }}>{val}</div>
  </motion.div>
);

const Wallet = () => {
  const [activeTab, setActiveTab] = useState("balance");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const {
    FetchUserData, HandleFetchUserData, createWithdraw, withdrawHistory,
    fetchWithdrawHistory, loading, setloading, createReinvestment,
  } = useAppContext();
  const [showNotice, setShowNotice] = useState(true);

  useEffect(() => {
    HandleFetchUserData();
  }, [HandleFetchUserData]);

  const withdrawNum = useMemo(() => Number(withdrawAmount || 0), [withdrawAmount]);
  const user = FetchUserData || {};
  const availableBalance = Number(user.totalEarnings || 0);
  const [reinvestAmount, setReinvestAmount] = useState("");
  const [pendingReinvest, setPendingReinvest] = useState(0);

  const withdrawFee = useMemo(() => {
    const fee = withdrawNum * 0.10;
    return Number.isFinite(fee) ? fee : 0;
  }, [withdrawNum]);

  const withdrawFinal = useMemo(() => {
    const net = withdrawNum - withdrawFee;
    return Number.isFinite(net) ? net : 0;
  }, [withdrawNum, withdrawFee]);

  const canWithdraw = useMemo(() => {
    if (!withdrawAmount) return false;
    if (withdrawNum < 20) return false;
    if (withdrawNum > availableBalance) return false;
    return true;
  }, [withdrawAmount, withdrawNum, availableBalance]);

  const maskWallet = (addr = "") => {
    if (!addr) return "—";
    if (addr.length <= 8) return addr;
    return `${addr.slice(0, 6)}••••${addr.slice(-4)}`;
  };
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);
  const [showReinvestConfirm, setShowReinvestConfirm] = useState(false);
  useEffect(() => {
    if (activeTab === "history") {
      fetchWithdrawHistory();
    }
  }, [activeTab, fetchWithdrawHistory]);

  const pendingSum = useMemo(() => {
    const list = Array.isArray(withdrawHistory) ? withdrawHistory : [];
    return list
      .filter((w) => ["pending", "processing"].includes(String(w.status || "").toLowerCase()))
      .reduce((sum, w) => sum + Number(w.amount || w.amountUSD || 0), 0);
  }, [withdrawHistory]);

  const handleWithdraw = () => {
    createWithdraw(withdrawAmount);
  };

  if (!FetchUserData && !loading) {
    setloading(true);
  }

  const reinvestNum = Number(reinvestAmount || 0);
  const canReinvest = reinvestNum > 0 && reinvestNum <= availableBalance;

  const tabs = [
    { id: "balance", label: "Overview", icon: <FaWallet /> },
    { id: "history", label: "History", icon: <FaHistory /> },
    { id: "withdraw", label: "Withdraw", icon: <FaArrowCircleUp /> },
    { id: "reinvest", label: "Reinvest", icon: <FaRedo /> },
  ];

  return (
    <div style={{
      minHeight: "100vh", color: T.textPrimary,
      fontFamily: "system-ui, -apple-system, sans-serif",
      paddingBottom: 80,
    }}>

      {/* ══════════ HEADER ══════════ */}
      <div style={{ padding: "clamp(20px, 4vw, 30px) clamp(16px, 4vw, 24px) clamp(30px, 5vw, 40px)", textAlign: "center" }}>
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 16 }}
          style={{
            width: "clamp(64px, 10vw, 76px)", height: "clamp(64px, 10vw, 76px)", margin: "0 auto 18px",
            background: `linear-gradient(135deg,#1e40af,${T.blue})`,
            borderRadius: 26,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 16px 36px rgba(59,130,246,0.35)",
          }}
        >
          <FaWallet size={"clamp(24px, 5vw, 30px)"} color="#fff" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h1 style={{
            fontSize: "clamp(1.5rem, 5vw, 2.2rem)",
            fontWeight: 900, color: T.textPrimary,
            textTransform: "uppercase", letterSpacing: "-0.03em", margin: 0,
          }}>
            Capital <span style={{ color: T.accent }}>Hub</span>
          </h1>
          <p style={{ fontSize: "clamp(8px, 2vw, 9px)", fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.3em", marginTop: 6 }}>
            Transaction Node: Verified
          </p>
        </motion.div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 clamp(16px, 4vw, 20px)" }}>

        {/* ══════════ BALANCE OVERVIEW ══════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          style={{
            background: T.bgCard,
            border: `1px solid rgba(234,179,8,0.2)`,
            borderRadius: 36, padding: "clamp(24px, 5vw, 40px) clamp(16px, 4vw, 28px)",
            textAlign: "center", position: "relative", overflow: "hidden",
            boxShadow: "0 24px 70px rgba(0,0,0,0.5)",
            marginBottom: 20,
          }}
        >
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
          }} />
          <div style={{
            position: "absolute", top: "-30%", left: "50%", transform: "translateX(-50%)",
            width: "clamp(200px, 50vw, 320px)", height: "clamp(200px, 50vw, 320px)", borderRadius: "50%",
            background: "radial-gradient(ellipse,rgba(234,179,8,0.08) 0%,transparent 70%)",
            pointerEvents: "none",
          }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.3em", marginBottom: 12 }}>
              Available Liquidity
            </p>
            <motion.div
              key={availableBalance}
              initial={{ scale: 1.06 }}
              animate={{ scale: 1 }}
              style={{
                fontSize: "clamp(2rem, 8vw, 3.6rem)",
                fontWeight: 900, color: T.textPrimary,
                letterSpacing: "-0.04em",
                wordBreak: "break-word"
              }}
            >
              ${availableBalance.toFixed(2)}
            </motion.div>
            <div style={{ marginTop: 20 }}>
              <span style={{
                padding: "6px 18px", borderRadius: 999,
                background: T.accentDim,
                border: `1px solid rgba(234,179,8,0.3)`,
                color: T.accent, fontSize: 9, fontWeight: 900,
                textTransform: "uppercase", letterSpacing: "0.15em",
                display: "inline-block"
              }}>
                Secure Asset Vault
              </span>
            </div>
          </div>
        </motion.div>

        {/* ══════════ MINI STAT GRID (AUTO-STACKING) ══════════ */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 16,
          marginBottom: 28,
        }}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: 22, padding: "clamp(16px, 4vw, 22px) 16px",
              display: "flex", flexDirection: "column", alignItems: "center",
              boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
            }}
          >
            <p style={{ fontSize: "clamp(8px, 2vw, 9px)", fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 6 }}>
              Total Withdrawn
            </p>
            <div style={{ fontSize: "clamp(1.2rem, 4vw, 1.4rem)", fontWeight: 900, color: T.textPrimary }}>
              ${(user.WithdrwalAmt || 0).toFixed(2)}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: 22, padding: "clamp(16px, 4vw, 22px) 16px",
              display: "flex", flexDirection: "column", alignItems: "center",
              boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
            }}
          >
            <p style={{ fontSize: "clamp(8px, 2vw, 9px)", fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 6 }}>
              Locked/Pending
            </p>
            <div style={{ fontSize: "clamp(1.2rem, 4vw, 1.4rem)", fontWeight: 900, color: T.blue }}>
              ${Number(pendingSum || 0).toFixed(2)}
            </div>
          </motion.div>
        </div>

        {/* ══════════ TABS (WRAP) ══════════ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          style={{
            display: "flex", flexWrap: "wrap", gap: 6,
            background: "rgba(0,0,0,0.3)",
            border: `1px solid ${T.border}`,
            borderRadius: 20, padding: 6,
            marginBottom: 28,
          }}
        >
          {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: "1 1 auto",
                  padding: "12px 10px", borderRadius: 14,
                  fontSize: "clamp(9px, 2.5vw, 10px)", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  border: "none", cursor: "pointer",
                  background: isActive ? T.accent : "transparent",
                  color: isActive ? "#0a0d14" : T.textMuted,
                  boxShadow: isActive ? "0 6px 20px rgba(234,179,8,0.3)" : "none",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = T.textSecondary; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = T.textMuted; }}
              >
                {tab.icon} {tab.label}
              </button>
            );
          })}
        </motion.div>

        {/* ══════════ CONTENT AREA ══════════ */}
        <AnimatePresence mode="wait">
          {activeTab === "balance" && (
            <motion.div
              key="balance"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                gap: 16,
              }}
            >
              <QuickStat label="Total Points" val={user.TotalPoints} color={T.blue} delay={0} />
              <QuickStat label="Ref Revenue" val={`$${user.earnings?.referralCommission}`} color={T.purple} delay={0.06} />
              <QuickStat label="ROI Yield" val={`$${user.earnings?.roi?.toFixed(2)}`} color={T.textPrimary} delay={0.12} />
              <QuickStat label="Pend Reinvest" val={`$${pendingReinvest.toFixed(2)}`} color={T.success} delay={0.18} />
            </motion.div>
          )}

          {activeTab === "history" && (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <FaHistory style={{ color: T.accent, fontSize: 11 }} />
                <h3 style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.2em", margin: 0 }}>
                  Transaction Logs
                </h3>
              </div>

              {withdrawHistory.length === 0 && (
                <div style={{
                  textAlign: "center", padding: "44px 0",
                  color: T.textMuted, fontSize: 12, fontWeight: 700,
                  background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 24,
                }}>
                  No transactions yet.
                </div>
              )}

              {withdrawHistory.map((w, idx) => {
                const isPaid = w.status === "paid";
                return (
                  <motion.div
                    key={w._id}
                    initial={{ opacity: 0, x: idx % 2 === 0 ? -24 : 24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(idx, 6) * 0.05, duration: 0.5 }}
                    style={{
                      background: T.bgCard,
                      border: `1px solid ${T.border}`,
                      padding: "clamp(12px, 3vw, 18px)", borderRadius: 22,
                      display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12,
                      boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
                      transition: "border-color 0.2s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = isPaid ? "rgba(34,197,94,0.35)" : "rgba(234,179,8,0.3)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "1 1 auto", minWidth: 0 }}>
                      <div style={{
                        width: 42, height: 42, borderRadius: 14, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 16,
                        background: isPaid ? T.successDim : T.accentDim,
                        color: isPaid ? T.success : T.accent,
                        border: `1px solid ${isPaid ? "rgba(34,197,94,0.25)" : "rgba(234,179,8,0.25)"}`,
                      }}>
                        {isPaid ? <FaCheckCircle /> : <FaClock />}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: "clamp(11px, 3vw, 13px)", fontWeight: 900, color: T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          Withdrawal Request
                        </div>
                        <div style={{ fontSize: "clamp(8px, 2vw, 9px)", color: T.textMuted, fontWeight: 700, textTransform: "uppercase", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {new Date(w.createdAt).toLocaleDateString()} • {maskWallet(w.toAddress)}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: "clamp(13px, 3.5vw, 15px)", fontWeight: 900, color: T.textPrimary }}>
                        -${Number(w.amount || 0).toFixed(2)}
                      </div>
                      <div style={{
                        fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.1em",
                        color: isPaid ? T.success : T.warning, marginTop: 3,
                      }}>
                        {w.status}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {activeTab === "withdraw" && (
            <motion.div
              key="withdraw"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{ maxWidth: 460, margin: "0 auto" }}
            >
              <div style={{
                background: T.bgCard,
                border: `1px solid ${T.border}`,
                borderRadius: 32, padding: "clamp(20px, 4vw, 30px) clamp(16px, 4vw, 26px)",
                boxShadow: "0 20px 56px rgba(0,0,0,0.45)",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg,${T.blue},transparent)`,
                }} />

                <div style={{ display: "flex", flexDirection: "column", gap: 20, position: "relative", zIndex: 1 }}>
                  <div>
                    <label style={{
                      display: "block", fontSize: 9, fontWeight: 900, color: T.textMuted,
                      textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 12, textAlign: "center",
                    }}>
                      Enter Settlement Amount ($)
                    </label>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(String(Math.floor(Number(e.target.value))))}
                      placeholder="0.00"
                      style={{
                        width: "100%", padding: "clamp(16px, 4vw, 22px) 16px",
                        background: "rgba(0,0,0,0.3)",
                        border: `2px solid ${T.border}`,
                        borderRadius: 22,
                        fontSize: "clamp(1.5rem, 5vw, 1.8rem)", fontWeight: 900,
                        color: T.textPrimary, textAlign: "center",
                        outline: "none", boxSizing: "border-box",
                        transition: "border-color 0.2s",
                      }}
                      onFocus={e => e.currentTarget.style.borderColor = T.blue}
                      onBlur={e => e.currentTarget.style.borderColor = T.border}
                    />
                  </div>

                  <div style={{
                    background: "rgba(0,0,0,0.25)",
                    border: `1px solid ${T.border}`,
                    borderRadius: 18, padding: 18,
                    display: "flex", flexDirection: "column", gap: 10,
                  }}>
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8, fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                      <span style={{ color: T.textMuted }}>Processing Fee (10%)</span>
                      <span style={{ color: T.warning }}>-${withdrawFee.toFixed(2)}</span>
                    </div>
                    <div style={{ height: 1, background: T.border }} />
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", letterSpacing: "0.12em" }}>
                        Net Received
                      </span>
                      <span style={{ fontSize: "clamp(1.2rem, 4vw, 1.4rem)", fontWeight: 900, color: T.blue }}>
                        ${withdrawFinal.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <motion.button
                    whileTap={canWithdraw ? { scale: 0.96 } : {}}
                    whileHover={canWithdraw ? { y: -2 } : {}}
                    // onClick={() => setShowWithdrawConfirm(true)}
                    onClick={() => setShowNotice(true)}
                    disabled={!canWithdraw}
                    style={{
                      width: "100%", padding: "18px 0",
                      background: canWithdraw ? T.blue : "rgba(255,255,255,0.05)",
                      color: canWithdraw ? "#fff" : T.textMuted,
                      fontWeight: 900, borderRadius: 18,
                      border: canWithdraw ? "none" : `1px solid ${T.border}`,
                      textTransform: "uppercase", letterSpacing: "0.2em", fontSize: 11,
                      cursor: canWithdraw ? "pointer" : "not-allowed",
                      boxShadow: canWithdraw ? "0 16px 36px rgba(59,130,246,0.35)" : "none",
                      transition: "all 0.2s",
                    }}
                  >
                    Execute Withdrawal
                  </motion.button>

                  <div style={{
                    padding: 16, borderRadius: 18,
                    background: T.blueDim,
                    border: `1px solid rgba(59,130,246,0.25)`,
                    display: "flex", gap: 12, alignItems: "flex-start",
                  }}>
                    <FaInfoCircle style={{ color: T.blue, marginTop: 2, flexShrink: 0 }} size={13} />
                    <div style={{ fontSize: 10, color: "#bfdbfe", fontWeight: 700, lineHeight: 1.6, textTransform: "uppercase" }}>
                      Settlement window open on 1st & 15th. Minimum threshold: $20. Processing time: 24-48h.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === "reinvest" && (
            <motion.div
              key="reinvest"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{ maxWidth: 460, margin: "0 auto" }}
            >
              <div style={{
                background: T.bgCard,
                border: `1px solid rgba(34,197,94,0.25)`,
                borderRadius: 32, padding: "clamp(20px, 4vw, 30px) clamp(16px, 4vw, 26px)",
                boxShadow: "0 20px 56px rgba(0,0,0,0.45)",
                position: "relative", overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: 3,
                  background: `linear-gradient(90deg,${T.success},transparent)`,
                }} />

                <div style={{ marginBottom: 24, textAlign: "center", position: "relative", zIndex: 1 }}>
                  <h3 style={{ fontSize: "1.2rem", fontWeight: 900, color: T.textPrimary, margin: "0 0 8px", textTransform: "uppercase" }}>
                    Increase Active Plan
                  </h3>
                  <p style={{ fontSize: 12, color: T.textMuted, margin: 0 }}>
                    Add more capital to your current investment plan
                  </p>
                </div>

                <input
                  type="number"
                  value={reinvestAmount}
                  onChange={(e) => setReinvestAmount(e.target.value)}
                  placeholder="Enter Amount"
                  style={{
                    width: "100%", padding: "clamp(16px, 4vw, 22px) 16px",
                    background: "rgba(0,0,0,0.3)",
                    border: `2px solid ${T.border}`,
                    borderRadius: 22,
                    fontSize: "clamp(1.5rem, 5vw, 1.8rem)", fontWeight: 900,
                    color: T.textPrimary, textAlign: "center",
                    outline: "none", boxSizing: "border-box",
                    transition: "border-color 0.2s",
                    position: "relative", zIndex: 1,
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = T.success}
                  onBlur={e => e.currentTarget.style.borderColor = T.border}
                />

                <div style={{
                  marginTop: 18, background: "rgba(0,0,0,0.25)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 18, padding: 18,
                  position: "relative", zIndex: 1,
                }}>
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ color: T.textMuted, fontSize: 12, fontWeight: 700 }}>Available Balance</span>
                    <span style={{ fontWeight: 900, color: T.blue, fontSize: 13 }}>
                      ${availableBalance.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", marginTop: 12, gap: 8 }}>
                    <span style={{ color: T.textMuted, fontSize: 12, fontWeight: 700 }}>Reinvest</span>
                    <span style={{ fontWeight: 900, color: T.success, fontSize: 13 }}>
                      ${pendingReinvest.toFixed(2)}
                    </span>
                  </div>
                </div>

                <motion.button
                  whileTap={canReinvest ? { scale: 0.96 } : {}}
                  whileHover={canReinvest ? { y: -2 } : {}}
                  onClick={() => setShowReinvestConfirm(true)}
                  disabled={!reinvestAmount}
                  style={{
                    width: "100%", marginTop: 20, padding: "18px 0",
                    background: reinvestAmount ? T.success : "rgba(255,255,255,0.05)",
                    color: reinvestAmount ? "#0a0d14" : T.textMuted,
                    fontWeight: 900, borderRadius: 18,
                    border: reinvestAmount ? "none" : `1px solid ${T.border}`,
                    textTransform: "uppercase", letterSpacing: "0.2em", fontSize: 11,
                    cursor: reinvestAmount ? "pointer" : "not-allowed",
                    boxShadow: reinvestAmount ? "0 16px 36px rgba(34,197,94,0.3)" : "none",
                    transition: "all 0.2s",
                    position: "relative", zIndex: 1,
                  }}
                >
                  Reinvest Funds
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showWithdrawConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.75)",
                backdropFilter: "blur(8px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                padding: 20,
              }}
            >
              <motion.div
                initial={{ scale: .8, y: 40 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: .8, y: 40 }}
                transition={{ type: "spring", stiffness: 280, damping: 20 }}
                style={{
                  width: "100%",
                  maxWidth: 420,
                  background: T.bgCard,
                  border: `1px solid rgba(234,179,8,.25)`,
                  borderRadius: 28,
                  overflow: "hidden",
                  boxShadow: "0 25px 70px rgba(0,0,0,.6)",
                }}
              >
                {/* Top Accent */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg,${T.accent},${T.blue})`,
                  }}
                />

                <div style={{ padding: 28, textAlign: "center" }}>
                  {/* Icon */}
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      margin: "0 auto 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: T.accentDim,
                      border: `2px solid rgba(234,179,8,.35)`,
                      fontSize: 34,
                    }}
                  >
                    💰
                  </div>

                  <h2
                    style={{
                      color: T.textPrimary,
                      fontWeight: 900,
                      marginBottom: 12,
                    }}
                  >
                    Confirm Withdrawal
                  </h2>

                  <p
                    style={{
                      color: T.textSecondary,
                      lineHeight: 1.7,
                      marginBottom: 20,
                    }}
                  >
                    You are about to withdraw
                  </p>

                  <div
                    style={{
                      background: T.accentDim,
                      border: `1px solid rgba(234,179,8,.25)`,
                      padding: 18,
                      borderRadius: 18,
                      marginBottom: 28,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 34,
                        fontWeight: 900,
                        color: T.accent,
                      }}
                    >
                      ${withdrawAmount}
                    </div>

                    <div
                      style={{
                        marginTop: 6,
                        color: T.textMuted,
                        fontSize: 12,
                      }}
                    >
                      Net Receive: ${withdrawFinal.toFixed(2)}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                    }}
                  >

                    <button
                      onClick={() => setShowWithdrawConfirm(false)}
                      style={{
                        flex: 1,
                        padding: 15,
                        borderRadius: 16,
                        background: "transparent",
                        border: `1px solid ${T.border}`,
                        color: T.textSecondary,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() => {
                        setShowWithdrawConfirm(false);
                        handleWithdraw();
                      }}
                      style={{
                        flex: 1,
                        padding: 15,
                        borderRadius: 16,
                        border: "none",
                        background: T.accent,
                        color: "#111",
                        fontWeight: 900,
                        cursor: "pointer",
                        boxShadow: "0 10px 25px rgba(234,179,8,.35)",
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* reinvestmodal 
 */}
        <AnimatePresence>
          {showReinvestConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.75)",
                backdropFilter: "blur(8px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                padding: 20,
              }}
            >
              <motion.div
                initial={{ scale: .8, y: 40 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: .8, y: 40 }}
                transition={{ type: "spring", stiffness: 280, damping: 20 }}
                style={{
                  width: "100%",
                  maxWidth: 420,
                  background: T.bgCard,
                  border: `1px solid rgba(34,197,94,.25)`,
                  borderRadius: 28,
                  overflow: "hidden",
                  boxShadow: "0 25px 70px rgba(0,0,0,.6)",
                }}
              >
                {/* Top Accent */}
                <div
                  style={{
                    height: 5,
                    background: `linear-gradient(90deg,${T.success},${T.blue})`,
                  }}
                />

                <div style={{ padding: 28, textAlign: "center" }}>
                  {/* Icon */}
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      margin: "0 auto 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: T.successDim,
                      border: "2px solid rgba(34,197,94,.35)",
                      fontSize: 34,
                    }}
                  >
                    <FaRedo color={T.success} />
                  </div>

                  <h2
                    style={{
                      color: T.textPrimary,
                      fontWeight: 900,
                      marginBottom: 12,
                    }}
                  >
                    Confirm Reinvestment
                  </h2>

                  <p
                    style={{
                      color: T.textSecondary,
                      lineHeight: 1.7,
                      marginBottom: 20,
                    }}
                  >
                    You are about to reinvest
                  </p>

                  <div
                    style={{
                      background: T.successDim,
                      border: "1px solid rgba(34,197,94,.25)",
                      padding: 18,
                      borderRadius: 18,
                      marginBottom: 28,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 34,
                        fontWeight: 900,
                        color: T.success,
                      }}
                    >
                      ${reinvestAmount}
                    </div>

                    <div
                      style={{
                        marginTop: 6,
                        color: T.textMuted,
                        fontSize: 12,
                      }}
                    >
                      This amount will be added to your active investment plan.
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                    }}
                  >
                    <button
                      onClick={() => setShowReinvestConfirm(false)}
                      style={{
                        flex: 1,
                        padding: 15,
                        borderRadius: 16,
                        background: "transparent",
                        border: `1px solid ${T.border}`,
                        color: T.textSecondary,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() => {
                        setShowReinvestConfirm(false);
                        createReinvestment(reinvestAmount);
                      }}
                      style={{
                        flex: 1,
                        padding: 15,
                        borderRadius: 16,
                        border: "none",
                        background: T.success,
                        color: "#fff",
                        fontWeight: 900,
                        cursor: "pointer",
                        boxShadow: "0 10px 25px rgba(34,197,94,.35)",
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
          {showNotice && (
        <WithdrawalNoticeModal
          onClose={() => setShowNotice(false)}
        />
      )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Wallet;